// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type PositionFormValues } from '@client/banks/hooks/use-position-form';
import type {
  LinkedLiquidityGate,
  LinkedLiquiditySectionApi,
} from '@client/banks/components/shared/linked-liquidity-section';
import { getBankCurrencySymbol } from '@client/banks/utils/bank-currency';
import { getBankPositionAssetTypeOptions } from '@client/banks/utils/bank-position-asset-type-label';
import {
  mapAssetsInfoPriceToPositionFields,
  type mapAssetsSearchToPositionFields,
} from '@client/banks/utils/map-assets-search-to-position';
import { computePositionInvestment } from '@client/banks/utils/position-valuation';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import dayjs from 'dayjs';
import { type MutableRefObject, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type PositionFormOption = {
  value: string;
  label: string;
};

export type PositionFormProps = {
  formId: string;
  onSubmit: (data: PositionFormValues) => void | Promise<void>;
  currencyOptions: readonly PositionFormOption[];
  accountIbanOptions: readonly PositionFormOption[];
  defaultCurrency: string;
  linkedLiquidityApiRef?: MutableRefObject<LinkedLiquiditySectionApi | null>;
  onLinkedLiquidityGateChange?: (gate: LinkedLiquidityGate) => void;
};

export const usePositionForm = ({
  currencyOptions,
  accountIbanOptions,
  defaultCurrency,
  linkedLiquidityApiRef,
  onLinkedLiquidityGateChange,
  ..._
}: PositionFormProps) => {
  const { control, handleSubmit, setValue } = useFormContext<PositionFormValues>();

  const { t } = useTranslation();

  const [fetchAssetsInfo] = assetsApi.useLazyAssetsInfoQuery();

  const quantity = useWatch({ control, name: 'quantity' }) ?? 0;
  const price = useWatch({ control, name: 'price' }) ?? 0;
  const isin = useWatch({ control, name: 'isin' }) ?? '';
  const currency = useWatch({ control, name: 'currency' });

  const currencyPrefix = useMemo(() => getBankCurrencySymbol(currency), [currency]);

  const marketValue = useMemo(
    () => computePositionInvestment(quantity, price),
    [price, quantity],
  );

  const assetTypeOptions = useMemo(() => getBankPositionAssetTypeOptions(t), [t]);

  const todayMaxDate = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  const onAssetSelect = useCallback(
    async (fields: ReturnType<typeof mapAssetsSearchToPositionFields>) => {
      setValue('isin', fields.isin, { shouldValidate: true });
      setValue('name', fields.name, { shouldValidate: true });
      setValue('assetType', fields.assetType, { shouldValidate: true });

      if (!fields.ticker || !fields.infoAssetType) {
        return;
      }

      try {
        const infoList = await fetchAssetsInfo({
          params: { detail: true, currency },
          payload: [fields.ticker],
          assetType: fields.infoAssetType,
        }).unwrap();

        const info = infoList[0];

        if (!info) {
          return;
        }

        const priceFields = mapAssetsInfoPriceToPositionFields(info);

        if (priceFields.price !== undefined && priceFields.price !== null) {
          setValue('price', priceFields.price, { shouldValidate: true });
        }

        if (priceFields.snapshotDate) {
          setValue('snapshotDate', priceFields.snapshotDate, {
            shouldValidate: true,
          });
        }
      } catch {
        // Leave price / valuation date for manual entry when assets-info fails.
      }
    },
    [currency, fetchAssetsInfo, setValue],
  );

  const onIsinChange = useCallback(
    (value: string) => {
      setValue('isin', value, { shouldValidate: true });
    },
    [setValue],
  );

  return {
    control,
    handleSubmit,
    t,
    currencyPrefix,
    assetTypeOptions,
    todayMaxDate,
    marketValue,
    onAssetSelect,
    onIsinChange,
    isin,
    currencyOptions,
    accountIbanOptions,
    defaultCurrency,
    linkedLiquidityApiRef,
    onLinkedLiquidityGateChange,
  };
};
